import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  useToast,
  useColorModeValue,
  Spinner,
  SimpleGrid,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Image,
  Textarea,
  Card,
  CardBody,
  Alert,
  AlertIcon
} from '@chakra-ui/react';
import { getChecklistById, type PreDepartureChecklistData } from '../../utils/checklistUtils';
import ImageViewerModal from '../shared/ImageViewerModal';
import { usePhotoUrl } from '../../utils/misc';

// Subcomponent for rendering photo from storage path using usePhotoUrl
interface PhotoBoxProps {
  storagePath: string;
  altText: string;
  compact: boolean;
  onImageClick: (url: string, title: string) => void;
}

const PhotoBox: React.FC<PhotoBoxProps> = ({ storagePath, altText, compact, onImageClick }) => {
  const { url, loading, error } = usePhotoUrl(storagePath);
  return (
    <Box>
      <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
        Photo
      </Text>
      {loading ? (
        <Spinner size="sm" color="blue.500" />
      ) : error ? (
        <Text fontSize="xs" color="red.500">Failed to load photo</Text>
      ) : url ? (
        <Image
          src={url}
          alt={altText}
          maxH={compact ? "150px" : "200px"}
          objectFit="contain"
          borderRadius="md"
          cursor="pointer"
          onClick={() => onImageClick(url, altText)}
        />
      ) : (
        <Text fontSize="xs" color="gray.400">No photo available</Text>
      )}
    </Box>
  );
};

interface ChecklistViewerProps {
  checklistId: string | null;
  showHeader?: boolean;
  compact?: boolean;
}

const ChecklistViewer: React.FC<ChecklistViewerProps> = ({ 
  checklistId, 
  showHeader = true, 
  compact = false 
}) => {
  const toast = useToast();
  
  // Color mode values
  const accordionBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  const [checklist, setChecklist] = useState<PreDepartureChecklistData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Image modal state
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalUrl, setImageModalUrl] = useState<string>('');
  const [imageModalTitle, setImageModalTitle] = useState<string>('Image');

  // Image modal handlers
  const handleImageClick = (url: string, title: string) => {
    setImageModalUrl(url);
    setImageModalTitle(title);
    setImageModalOpen(true);
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
    setTimeout(() => {
      setImageModalUrl('');
      setImageModalTitle('Image');
    }, 300);
  };

  // Load checklist when checklistId changes
  useEffect(() => {
    const loadChecklist = async () => {
      if (!checklistId) {
        setChecklist(null);
        return;
      }
      
      try {
        setIsLoading(true);
        setError(null);
        
        // Get checklist by ID
        const checklistData = await getChecklistById(checklistId);
        setChecklist(checklistData);
        
      } catch (error) {
        console.error('Error loading checklist:', error);
        setError('Failed to load checklist data');
        toast({
          title: 'Error Loading Checklist',
          description: 'Failed to load checklist. Please try again.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadChecklist();
  }, [checklistId, toast]);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Unknown';
    
    try {
      // Handle Firestore Timestamp
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'orange';
      case 'ongoing':
        return 'blue';
      case 'completed':
        return 'green';
      default:
        return 'gray';
    }
  };

  // Loading State
  if (isLoading) {
    return (
      <VStack spacing={4} align="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text fontSize="lg" fontWeight="semibold">Loading Checklist...</Text>
        <Text fontSize="sm" color="gray.600">Please wait while we fetch the checklist data</Text>
      </VStack>
    );
  }

  // Error State
  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <Text>{error}</Text>
      </Alert>
    );
  }

  // No checklist ID provided
  if (!checklistId) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Text>No checklist selected</Text>
      </Alert>
    );
  }

  // No checklist found
  if (!checklist) {
    return (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <Text>Checklist not found</Text>
      </Alert>
    );
  }

  return (
    <Box
      maxH={{ base: "70vh", md: "80vh" }}
      overflowY="auto"
      overflowX="hidden"
      css={{
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          width: '10px',
          background: 'rgba(0, 0, 0, 0.05)',
          borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: 'rgba(0, 0, 0, 0.3)',
        },
      }}
      pr={2}
    >
      <VStack spacing={compact ? 4 : 6} align="stretch" pb={4}>
        {/* Header */}
        {showHeader && (
          <VStack spacing={2} align="start">
            <Text fontSize="lg" fontWeight="bold">
              Checklist Details
            </Text>
            <HStack spacing={4}>
              <Badge colorScheme="blue">
                {checklist.plateNumber}
              </Badge>
              <Badge colorScheme={getStatusColor(checklist.status)}>
                {checklist.status}
              </Badge>
              {checklist.overallStatus && (
                <Badge colorScheme={checklist.overallStatus === 'pass' ? 'green' : 'red'}>
                  {checklist.overallStatus}
                </Badge>
              )}
            </HStack>
          </VStack>
        )}

        {/* Basic Info */}
        <VStack spacing={3} align="stretch">
          <Text fontSize="md" fontWeight="semibold">Basic Information</Text>
          <SimpleGrid columns={compact ? 1 : 2} spacing={4}>
            <Box>
              <Text fontSize="xs" fontWeight="semibold" color="gray.600">Company</Text>
              <Text fontSize="sm">{checklist.companyName}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" fontWeight="semibold" color="gray.600">Completed By</Text>
              <Text fontSize="sm">{checklist.completed_by}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" fontWeight="semibold" color="gray.600">Updated</Text>
              <Text fontSize="sm">{formatDate(checklist.updated_at)}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" fontWeight="semibold" color="gray.600">Progress</Text>
              <Text fontSize="sm">
                {checklist.completedRequired}/{checklist.totalRequired} required items
              </Text>
            </Box>
            {checklist.wayBillNumber && (
              <Box>
                <Text fontSize="xs" fontWeight="semibold" color="gray.600">Waybill Number</Text>
                <Text fontSize="sm">{checklist.wayBillNumber}</Text>
              </Box>
            )}
            <Box>
              <Text fontSize="xs" fontWeight="semibold" color="gray.600">Checklist ID</Text>
              <Text fontSize="sm">{checklist.customId}</Text>
            </Box>
          </SimpleGrid>
        </VStack>

        {/* Inspection Summary */}
        {checklist.inspectionSummary && (
          <Box>
            <Text fontSize="md" fontWeight="semibold" mb={2}>Inspection Summary</Text>
            <Textarea
              value={checklist.inspectionSummary}
              readOnly
              bg={accordionBg}
              resize="none"
              minH={compact ? "60px" : "80px"}
            />
          </Box>
        )}

        {/* Categories Accordion */}
        <Box>
          <Text fontSize="md" fontWeight="semibold" mb={4}>Checklist Items</Text>
          <Accordion allowMultiple>
            {checklist.categories?.map((category) => {
              const categoryStats = {
                total: category.items.length,
                completed: category.items.filter(item => item.checked).length,
                required: category.items.filter(item => item.required).length,
                completedRequired: category.items.filter(item => item.required && item.checked).length
              };

              return (
                <AccordionItem key={category.id}>
                  <AccordionButton bg={accordionBg} _expanded={{ bg: accordionBg }}>
                    <Box flex="1" textAlign="left">
                      <HStack justify="space-between" align="center">
                        <VStack align="start" spacing={1}>
                          <Text fontSize="md" fontWeight="semibold">
                            {category.name}
                          </Text>
                          <HStack spacing={3}>
                            <Badge colorScheme="blue" size="sm">
                              {categoryStats.completed}/{categoryStats.total} completed
                            </Badge>
                            <Badge colorScheme="green" size="sm">
                              {categoryStats.completedRequired}/{categoryStats.required} required
                            </Badge>
                          </HStack>
                        </VStack>
                        <AccordionIcon />
                      </HStack>
                    </Box>
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <VStack spacing={4} align="stretch">
                      {category.items.map((item) => (
                        <Card key={item.id} variant="outline" size="sm">
                          <CardBody>
                            <VStack spacing={3} align="stretch">
                              <HStack justify="space-between" align="start">
                                <HStack>
                                  <Box 
                                    w={4} 
                                    h={4} 
                                    borderRadius="sm" 
                                    bg={item.checked ? 'green.500' : 'gray.300'}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                  >
                                    {item.checked && (
                                      <Text color="white" fontSize="xs">✓</Text>
                                    )}
                                  </Box>
                                  <VStack align="start" spacing={1}>
                                    <Text fontSize="sm" fontWeight={item.required ? 'semibold' : 'normal'}>
                                      {item.text}
                                      {item.required && <Badge colorScheme="red" size="sm" ml={2}>Required</Badge>}
                                    </Text>
                                  </VStack>
                                </HStack>
                                {item.status && (
                                  <Badge 
                                    colorScheme={
                                      item.status === 'green' ? 'green' : 
                                      item.status === 'yellow' ? 'yellow' : 'red'
                                    } 
                                    variant="subtle"
                                  >
                                    {item.status === 'green' ? 'Pass' : 
                                     item.status === 'yellow' ? 'Needs Attention' : 'Fail'}
                                  </Badge>
                                )}
                              </HStack>

                              {/* Comment */}
                              {item.comment && (
                                <Box>
                                  <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                                    Comment
                                  </Text>
                                  <Text fontSize="sm" bg={accordionBg} p={2} borderRadius="md">
                                    {item.comment}
                                  </Text>
                                </Box>
                              )}

                              {/* Photo (from storage path) */}
                              {item.photoStoragePath && (
                                <PhotoBox 
                                  storagePath={item.photoStoragePath} 
                                  altText={`Photo for ${item.text}`} 
                                  compact={compact}
                                  onImageClick={handleImageClick}
                                />
                              )}

export default ChecklistViewer;
                            </VStack>
                          </CardBody>
                        </Card>
                      ))}
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
              );
            })}
          </Accordion>
        </Box>

        {/* Digital Signature */}
        {checklist.signatureDataURL && (
          <Box>
            <Text fontSize="md" fontWeight="semibold" mb={2}>Digital Signature</Text>
            <Image
              src={checklist.signatureDataURL}
              alt="Digital Signature"
              maxH={compact ? "100px" : "150px"}
              objectFit="contain"
              borderRadius="md"
              border="1px"
              borderColor={borderColor}
              p={2}
              cursor="pointer"
              onClick={() => handleImageClick(checklist.signatureDataURL!, 'Digital Signature')}
            />
          </Box>
        )}
      </VStack>

      {/* Image Viewer Modal */}
      <ImageViewerModal
        isOpen={imageModalOpen}
        onClose={closeImageModal}
        imageUrl={imageModalUrl}
        title={imageModalTitle}
      />
    </Box>
  );
};

export default ChecklistViewer;
